import React from "react";
import { Box, Grid, Typography, Link } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { motion } from "framer-motion";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SectionHeader from "./SectionHeader";
import CivixCard from "../../components/ui/CivixCard";
import { useJoinMotion } from "./JoinMotion";

const VERIFICATION_PATHS = [
  {
    id: "id-verified",
    title: "ID Verified",
    description: "Citizens verify via Stripe Identity (passport or driver's license).",
    bullets: ["Used in the app today", "Web alternative: liveness + reCAPTCHA"],
    icon: BadgeOutlinedIcon,
  },
  {
    id: "politician-review",
    title: "ID + Manual Review",
    description: "Public officials and candidates verify identity, then complete profile review.",
    bullets: ["Stripe Identity first", "CivixThread team approval"],
    icon: HowToRegOutlinedIcon,
  },
  {
    id: "document-verified",
    title: "Document Verified",
    description: "Organizations submit official documents for team review.",
    bullets: ["EIN, 501(c)(3), license, or incorporation", "Reviewed before approval"],
    icon: DescriptionOutlinedIcon,
  },
];

export default function VerificationSection() {
  const { stagger, item } = useJoinMotion();

  return (
    <Box
      id="verification"
      sx={{
        scrollMarginTop: { xs: 120, md: 128 },
        py: "var(--join-section-py)",
      }}
    >
      <SectionHeader
        variant="standard"
        title="Verification"
        description="How verification works by account type in the app today."
      />
      <Grid container spacing={2.5} component={motion.div} {...stagger}>
        {VERIFICATION_PATHS.map((path) => {
          const Icon = path.icon;
          return (
            <Grid item xs={12} md={4} key={path.id} component={motion.div} {...item}>
              <CivixCard variant="glass" sx={{ height: "100%" }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(4, 120, 87, 0.1)",
                    border: "1px solid rgba(4, 120, 87, 0.16)",
                    mb: 1.5,
                  }}
                >
                  <Icon sx={{ fontSize: 24, color: "var(--primary-color)" }} aria-hidden />
                </Box>
                <Typography
                  component="h3"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: 17, md: 18 },
                    color: "var(--black-color)",
                    mb: 0.75,
                    lineHeight: 1.25,
                  }}
                >
                  {path.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: 14, md: 15 },
                    color: "var(--dna-color-palette-black-400)",
                    lineHeight: 1.6,
                    mb: 1.5,
                  }}
                >
                  {path.description}
                </Typography>
                <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, mt: "auto" }}>
                  {path.bullets.map((bullet) => (
                    <Box
                      component="li"
                      key={bullet}
                      sx={{
                        display: "flex",
                        gap: 0.75,
                        alignItems: "flex-start",
                        fontSize: { xs: 13, md: 14 },
                        color: "var(--black-color)",
                        mb: 0.75,
                        lineHeight: 1.5,
                      }}
                    >
                      <CheckCircleOutlineIcon
                        sx={{ fontSize: 18, color: "var(--primary-color)", mt: 0.1, flexShrink: 0 }}
                        aria-hidden
                      />
                      {bullet}
                    </Box>
                  ))}
                </Box>
              </CivixCard>
            </Grid>
          );
        })}
      </Grid>

      <Box
        role="note"
        sx={{
          mt: 3,
          p: { xs: 2, md: 2.5 },
          borderRadius: "var(--radius-card)",
          border: "1px solid var(--join-border-muted)",
          bgcolor: "var(--join-surface-muted)",
          display: "flex",
          gap: 1.25,
          alignItems: "flex-start",
        }}
      >
        <InfoOutlinedIcon
          sx={{ fontSize: 22, color: "var(--primary-color)", mt: 0.15, flexShrink: 0 }}
          aria-hidden
        />
        <Box>
          <Typography sx={{ fontSize: { xs: 14, md: 15 }, color: "var(--black-color)", lineHeight: 1.65 }}>
            <Box component="span" sx={{ fontWeight: 700 }}>
              Citizenship note:
            </Box>{" "}
            &ldquo;Citizen&rdquo; is a platform role, not a statement of legal citizenship.
            CivixThread verifies identity documents and account eligibility — not legal status beyond each process.
          </Typography>
          <Link
            component={RouterLink}
            to="/feedback"
            sx={{
              display: "inline-block",
              mt: 1,
              fontSize: 14,
              fontWeight: 700,
              color: "var(--primary-color-hover)",
              minHeight: 44,
              lineHeight: "44px",
              "&:hover": { color: "var(--primary-color)" },
              "&:focus-visible": {
                outline: "none",
                boxShadow: "var(--join-focus-ring)",
                borderRadius: 1,
              },
            }}
          >
            Share feedback on verification
          </Link>
        </Box>
      </Box>
    </Box>
  );
}
